import { useEffect, useMemo, useRef, useState } from "react";
import { useNavigate } from "react-router-dom";
import api from "@/lib/api";
import Quiz from "@/components/Quiz";
import LetterCard from "@/components/LetterCard";
import WordCard from "@/components/WordCard";
import AlphabetLearnScreen from "@/components/AlphabetLearnScreen";
import ConfettiBurst from "@/components/ConfettiBurst";
import { makeStrictSequence } from "@/lib/quizSequence";
import { buildVocabQuestions } from "@/lib/vocabQuiz";

import {
  Minus,
  Plus,
  RotateCcw,
  ArrowLeft,
  RefreshCw,
  BrainCircuit,
  ChevronDown,
  ChevronRight,
  X,
  Volume2,
  ArrowRight,
  PartyPopper,
  CheckCircle2,
} from "lucide-react";
import { t } from "@/i18n";

const CATEGORIES = [
  { id: "alphabet", labelKey: "review.cat_alphabet" },
  { id: "mot", labelKey: "review.cat_mots" },
  { id: "lecon", labelKey: "review.cat_lecons" },
];

/* Mêmes bornes que Quiz.jsx : on ne passe jamais derrière le header
   ni derrière la barre d'onglets du bas. */
const NAV_TOP = "4rem";
const NAV_BOTTOM = "calc(3.4rem + env(safe-area-inset-bottom))";

const bucketOf = (q) => {
  const s = String(q.skill || "").toLowerCase();
  if (s === "alphabet") return "alphabet";
  if (s === "vocabulary") return "mot";
  return "lecon";
};

const getItemLabel = (q) => {
  if (bucketOf(q) === "alphabet" && q.char) {
    return `${q.char}  —  ${q.letter_name || q.translit || ""}`.trim();
  }
  const wd = q.word_detail;
  if (wd?.word_ar)
    return `${wd.word_ar}${wd.word_latin ? ` (${wd.word_latin})` : ""}`;
  return q.prompt || q.title || t("review.notion_a_reviser");
};

const getItemAnswer = (q) => {
  if (bucketOf(q) === "alphabet") {
    return [q.letter_name, q.translit].filter(Boolean).join(" · ");
  }
  const wd = q.word_detail;
  if (wd?.translation_en) return wd.translation_en;
  if (Array.isArray(q.options) && typeof q.answer === "number") {
    return q.options[q.answer];
  }
  return null;
};

const shuffle = (arr) => {
  const a = [...arr];
  for (let i = a.length - 1; i > 0; i--) {
    const j = Math.floor(Math.random() * (i + 1));
    [a[i], a[j]] = [a[j], a[i]];
  }
  return a;
};

const fillTo = (list, n) => {
  if (!list.length) return [];
  let out = shuffle(list);
  while (out.length < n) {
    out.push(list[Math.floor(Math.random() * list.length)]);
  }
  return shuffle(out).slice(0, n);
};

const playAudio = (kind, char) => {
  const a = new Audio(
    `/api/content/alphabet/audio/${kind}/${encodeURIComponent(char)}`,
  );
  a.play().catch(() => {});
};

/* Découverte du vocabulaire : tous les mots sur une page, comme
   AlphabetLearnScreen le fait pour les lettres. */
function VocabLearnScreen({ words, onDone, onExit }) {
  return (
    <div
      className="fixed inset-x-0 z-20 bg-background flex flex-col md:static md:z-auto"
      style={{ top: NAV_TOP, bottom: NAV_BOTTOM }}
    >
      <div className="shrink-0 px-4 pt-3 pb-2 flex items-center justify-between gap-3">
        <div>
          <div className="text-xs font-bold uppercase tracking-wider text-primary">
            {t("review.mots_decouverte", {
              count: words.length,
              s: words.length > 1 ? "s" : "",
            })}
          </div>
          <p className="text-xs text-muted-foreground mt-0.5">
            {t("review.revois_ces_mots_avant_de_les")}
          </p>
        </div>
        {onExit && (
          <button
            onClick={onExit}
            aria-label={t("alphabetlearnscreen.quitter")}
            className="shrink-0 w-8 h-8 rounded-full border grid place-items-center hover:bg-secondary transition"
          >
            <X size={15} />
          </button>
        )}
      </div>
      <div className="flex-1 min-h-0 overflow-y-auto px-4 space-y-2">
        {words.map((w) => (
          <div key={w.id} className="rounded-2xl border bg-card p-3">
            <WordCard word={w} />
          </div>
        ))}
        <div className="h-2" />
      </div>
      <div className="shrink-0 border-t bg-background/95 backdrop-blur-xl p-3">
        <button
          onClick={onDone}
          className="w-full max-w-md mx-auto py-3 rounded-2xl bg-primary text-primary-foreground font-bold flex items-center justify-center gap-2"
        >
          {t("review.commencer_le_quiz")} <ArrowRight size={17} />
        </button>
      </div>
    </div>
  );
}

export default function Review() {
  const nav = useNavigate();

  const [loading, setLoading] = useState(true);
  const [allQuestions, setAllQuestions] = useState([]);
  const [distractorPool, setDistractorPool] = useState([]);
  const [dailyRemaining, setDailyRemaining] = useState(50);
  const [count, setCount] = useState(20);
  const [showLearn, setShowLearn] = useState(true);

  const [selectedKeys, setSelectedKeys] = useState(new Set());
  const [expandedGroups, setExpandedGroups] = useState({
    alphabet: false,
    mot: false,
    lecon: false,
  });

  const [stages, setStages] = useState(null);
  const [stageIdx, setStageIdx] = useState(0);
  const [done, setDone] = useState(null);
  const [hint, setHint] = useState(null);
  const totalsRef = useRef({ correct: 0, total: 0, earned: 0 });

  useEffect(() => {
    api
      .get(`/progress/review?scope=mastered_review&limit=50`)
      .then((r) => {
        const qs = (r.data?.questions || []).map((q, idx) => ({
          ...q,
          uniqueKey: `${q.key || q.ref || "q"}-${idx}`,
        }));
        const remaining = Math.min(50, r.data?.daily_remaining ?? qs.length);
        setAllQuestions(qs);
        setDistractorPool(r.data?.distractor_pool || []);
        setSelectedKeys(new Set(qs.map((q) => q.uniqueKey)));
        setDailyRemaining(remaining);
        if (qs.length > 0 && qs.length < 20) setCount(qs.length);
      })
      .catch(() => setAllQuestions([]))
      .finally(() => setLoading(false));
  }, []);

  const grouped = useMemo(() => {
    const g = { alphabet: [], mot: [], lecon: [] };
    allQuestions.forEach((q) => g[bucketOf(q)].push(q));
    return g;
  }, [allQuestions]);

  const maxCount = Math.min(50, Math.max(1, dailyRemaining));

  const start = () => {
    console.log("BUILD_CHECK_2026");

    const chosen = allQuestions.filter((q) => selectedKeys.has(q.uniqueKey));
    if (!chosen.length) return;

    const target = Math.min(count, maxCount);
    const queue = fillTo(chosen, target);

    const alpha = queue.filter((q) => bucketOf(q) === "alphabet");
    const vocab = queue.filter((q) => bucketOf(q) === "mot");
    const other = queue.filter((q) => bucketOf(q) === "lecon");

    const next = [];

    // ── Alphabet ──
    if (alpha.length) {
      const byChar = {};
      alpha.forEach((q) => {
        const ch =
          q.char ||
          (String(q.key || "").startsWith("alpha:")
            ? String(q.key).split(":")[1]
            : null);
        if (ch && !byChar[ch]) {
          byChar[ch] = {
            char: ch,
            name: q.letter_name || "",
            translit: q.translit || "",
            sound: q.translit || "",
            hint: q.hint || "",
          };
        }
        q.char = ch;
      });
      const letters = Object.values(byChar);
      if (showLearn && letters.length) {
        next.push({ type: "learn-alpha", letters });
      }
      next.push({
        type: "quiz",
        kind: "alphabet",
        title: t("review.cat_alphabet"),
        letters: byChar,
        questions: makeStrictSequence(
          alpha.map((q) => ({
            char: q.char,
            prompt: q.prompt,
            options: q.options,
            answer: q.answer,
            skill: "alphabet",
            key: q.key,
            ref: q.ref,
          })),
        ),
      });
    }

    // ── Vocabulaire ──
    if (vocab.length) {
      const byId = {};
      vocab.forEach((q) => {
        const id = q.ref || q.key;
        const wd = q.word_detail;
        if (id && wd?.word_ar && !byId[id]) {
          byId[id] = {
            id,
            word_ar: wd.word_ar,
            word_latin: wd.word_latin || "",
            translation_en: wd.translation_en || "",
            root: wd.root || "",
            example_ar: wd.example_ar || "",
            example_fr: wd.example_fr || "",
          };
        }
      });
      const words = Object.values(byId);
      const queueWords = vocab.map((q) => byId[q.ref || q.key]).filter(Boolean);

      if (queueWords.length) {
        if (showLearn) next.push({ type: "learn-vocab", words });
        const wide =
          distractorPool.length >= 6 ? [...words, ...distractorPool] : words;
        next.push({
          type: "quiz",
          kind: "vocab",
          title: t("review.cat_vocabulaire"),
          words: byId,
          questions: buildVocabQuestions(queueWords, wide), // 2 args
        });
      }
    }

    // ── Leçons / grammaire ──
    if (other.length) {
      next.push({
        type: "quiz",
        kind: "lecon",
        title: t("review.cat_lecons_grammaire"),
        questions: other.map((q) => ({
          prompt: q.prompt,
          options: q.options,
          answer: q.answer,
          skill: q.skill || "grammar",
          key: q.key,
          ref: q.ref,
        })),
      });
    }

    if (!next.length) return;
    totalsRef.current = { correct: 0, total: 0, earned: 0 };
    setDone(null);
    setStageIdx(0);
    setStages(next);
  };

  const advance = () => {
    if (stageIdx + 1 < stages.length) {
      setStageIdx(stageIdx + 1);
    } else {
      setStages(null);
      setDone({ ...totalsRef.current });
      window.dispatchEvent(new CustomEvent("arabdex:dinar-update"));
    }
  };

  const onQuizDone = (r) => {
    totalsRef.current.correct += r?.correct || 0;
    totalsRef.current.total += r?.total || 0;
    totalsRef.current.earned += r?.earned || 0;
    advance();
  };

  const quitSession = () => {
    setStages(null);
    setStageIdx(0);
    setDone(null);
    setHint(null);
  };

  const toggleGroupSelection = (keys, isAllSelected) => {
    setSelectedKeys((prev) => {
      const next = new Set(prev);
      keys.forEach((k) => (isAllSelected ? next.delete(k) : next.add(k)));
      return next;
    });
  };

  const toggleItemSelection = (k) => {
    setSelectedKeys((prev) => {
      const next = new Set(prev);
      next.has(k) ? next.delete(k) : next.add(k);
      return next;
    });
  };

  // ───────────────────────── écrans ─────────────────────────

  if (loading) {
    return (
      <div className="flex flex-col items-center justify-center mt-32 text-muted-foreground animate-pulse">
        <RefreshCw className="animate-spin mb-4" size={32} />
        <p className="font-medium">
          {t("review.calcul_de_tes_revisions_optimales")}
        </p>
      </div>
    );
  }

  if (stages && !done) {
    const stage = stages[stageIdx];

    if (stage.type === "learn-alpha") {
      return (
        <AlphabetLearnScreen
          letters={stage.letters}
          onAudio={playAudio}
          onDone={advance}
          onExit={quitSession}
        />
      );
    }

    if (stage.type === "learn-vocab") {
      return (
        <VocabLearnScreen
          words={stage.words}
          onDone={advance}
          onExit={quitSession}
        />
      );
    }

    return (
      <>
        <Quiz
          key={`${stageIdx}-${stage.kind}`}
          questions={stage.questions}
          onDone={onQuizDone}
          rewardMode="review"
          showCelebration={false}
          title={stage.title}
          onExit={quitSession}
          onHint={(q) => {
            if (stage.kind === "alphabet") {
              const l = stage.letters?.[q.char];
              if (l) setHint({ kind: "letter", data: l });
            } else if (stage.kind === "vocab") {
              const id =
                q.ref ||
                String(q.key || "")
                  .replace(/^vocab:/, "")
                  .split(":")[0];
              const w = stage.words?.[id];
              if (w) setHint({ kind: "word", data: w });
            }
          }}
        />
        {hint && (
          <div
            className="fixed inset-0 z-[70] grid place-items-center bg-black/60 backdrop-blur-sm p-4"
            onClick={() => setHint(null)}
          >
            <div
              className="w-full max-w-sm rounded-3xl border bg-card p-6"
              onClick={(e) => e.stopPropagation()}
            >
              <div className="flex justify-end mb-1">
                <button
                  onClick={() => setHint(null)}
                  className="rounded-full p-1.5 hover:bg-muted"
                >
                  <X size={17} />
                </button>
              </div>
              {hint.kind === "letter" ? (
                <LetterCard letter={hint.data} onAudio={playAudio} />
              ) : (
                <WordCard word={hint.data} />
              )}
              <button
                onClick={() => setHint(null)}
                className="w-full mt-5 py-3 rounded-2xl bg-primary text-primary-foreground font-bold"
              >
                {t("exerciseblock.j_ai_compris")}
              </button>
            </div>
          </div>
        )}
      </>
    );
  }

  if (done) {
    const score = done.total
      ? Math.round((done.correct / done.total) * 100)
      : 0;
    return (
      <div className="max-w-xl mx-auto text-center mt-8 px-4">
        <div className="rounded-3xl border bg-card p-7 relative overflow-hidden">
          {done.earned > 0 && <ConfettiBurst />}
          <PartyPopper size={44} className="mx-auto mb-3 text-amber-500" />
          <h2 className="font-head text-2xl font-bold mb-1">
            {t("practiceconjugation.session_terminee")}
          </h2>
          <p className="text-sm text-muted-foreground mb-4">
            {done.correct} / {done.total} {t("myerrors.bonnes_reponses")}{" "}
            {score}%
          </p>
          <div className="text-5xl font-head font-extrabold text-amber-500 mb-1">
            +{done.earned} 🪙
          </div>
          <p className="text-xs text-muted-foreground mb-6">
            {score >= 80
              ? t("review.ces_notions_sont_repoussees_plus_loin")
              : t("review.les_erreurs_d_aujourd_hui_construisent")}
          </p>
          <div className="h-2.5 rounded-full bg-secondary overflow-hidden mb-6">
            <div
              className="h-full rounded-full transition-all duration-700"
              style={{
                width: `${score}%`,
                backgroundColor:
                  score >= 80 ? "#16A34A" : "hsl(var(--destructive))",
              }}
            />
          </div>
          <div className="grid grid-cols-2 gap-2">
            <button
              onClick={() => nav(0)}
              className="py-3 rounded-2xl border font-semibold flex items-center justify-center gap-2"
            >
              <RotateCcw size={15} /> {t("exerciseblock.continuer")}
            </button>
            <button
              onClick={() => nav("/dashboard")}
              className="py-3 rounded-2xl border font-semibold flex items-center justify-center gap-2"
            >
              <ArrowLeft size={15} /> {t("coursequestions.accueil")}
            </button>
          </div>
        </div>
      </div>
    );
  }

  if (!allQuestions.length) {
    return (
      <div className="max-w-xl mx-auto rounded-2xl border bg-card p-8 text-center shadow-sm mt-12">
        <CheckCircle2 size={48} className="mx-auto mb-3 text-green-600" />
        <h1 className="font-head text-2xl font-bold mb-2">
          {dailyRemaining === 0
            ? t("review.limite_quotidienne_atteinte")
            : t("review.aucune_revision_due")}
        </h1>
        <p className="text-muted-foreground mb-6">
          {dailyRemaining === 0
            ? t("review.tu_as_atteint_la_limite_de")
            : t("review.tu_as_traite_toutes_tes_revisions")}
        </p>
        <button
          onClick={() => nav("/dashboard")}
          className="px-5 py-2.5 rounded-xl bg-primary text-primary-foreground font-medium"
        >
          {t("review.retour_au_centre_d_apprentissage")}
        </button>
      </div>
    );
  }

  const selectedCount = selectedKeys.size;

  return (
    <div className="max-w-3xl mx-auto animate-in fade-in pb-12 px-4">
      <div className="flex items-center gap-3 mb-2">
        <BrainCircuit size={28} className="text-primary" />
        <h1 className="font-head text-3xl font-bold">
          {t("review.revisions_intelligentes")}
        </h1>
      </div>

      <div className="bg-secondary/50 border rounded-2xl p-5 mb-6">
        <p className="text-sm leading-relaxed">
          {t("review.la_repetition_espacee_te_fait_revoir")}{" "}
          <strong>{t("review.juste_avant_que_tu_risques_de")}</strong>.{" "}
          {t("review.seules_les_notions_cochees")}
        </p>
      </div>

      <div className="rounded-2xl border bg-card p-5 mb-4 space-y-4">
        <div className="flex flex-col sm:flex-row sm:items-center justify-between gap-4">
          <div>
            <h3 className="font-semibold mb-1">
              {t("review.combien_de_questions")}
            </h3>
            <p className="text-sm text-muted-foreground">
              {t("review.notions_cochees_boucle", {
                count: selectedCount,
                s: selectedCount !== 1 ? "s" : "",
              })}
            </p>
          </div>
          <div className="flex items-center gap-4 bg-secondary p-2 rounded-xl shrink-0">
            <button
              onClick={() => setCount((c) => Math.max(5, c - 5))}
              className="w-10 h-10 rounded-lg bg-background shadow-sm border grid place-items-center"
            >
              <Minus size={18} />
            </button>
            <span className="font-head text-2xl font-bold w-10 text-center tabular-nums">
              {Math.min(count, maxCount)}
            </span>
            <button
              onClick={() => setCount((c) => Math.min(maxCount, c + 5))}
              className="w-10 h-10 rounded-lg bg-background shadow-sm border grid place-items-center"
            >
              <Plus size={18} />
            </button>
          </div>
        </div>

        <label className="flex items-center justify-between gap-3 cursor-pointer border-t pt-4">
          <span className="text-sm">
            <span className="font-medium">
              {t("myerrors.voir_les_fiches_avant")}
            </span>
            <span className="block text-xs text-muted-foreground">
              {t("review.une_fiche_pour_chaque_lettre_et")}
            </span>
          </span>
          <input
            type="checkbox"
            checked={showLearn}
            onChange={(e) => setShowLearn(e.target.checked)}
            className="w-5 h-5 accent-primary shrink-0"
          />
        </label>
      </div>

      <div className="mb-6 space-y-3">
        <h3 className="font-semibold px-1">
          {t("review.que_veux_tu_reviser")}
        </h3>
        {CATEGORIES.map(({ id: catId, labelKey }) => {
          const qs = grouped[catId] || [];
          if (!qs.length) return null;

          const isExpanded = !!expandedGroups[catId];
          const groupKeys = qs.map((q) => q.uniqueKey);
          const selectedInGroup = groupKeys.filter((k) => selectedKeys.has(k));
          const isAllSelected = selectedInGroup.length === groupKeys.length;
          const isSomeSelected = selectedInGroup.length > 0 && !isAllSelected;

          return (
            <div
              key={catId}
              className="border rounded-xl bg-card overflow-hidden"
            >
              <div className="flex items-center p-3.5 bg-secondary/20 select-none">
                <div
                  className="flex items-center justify-center w-6 cursor-pointer"
                  onClick={(e) => {
                    e.stopPropagation();
                    toggleGroupSelection(groupKeys, isAllSelected);
                  }}
                >
                  <input
                    type="checkbox"
                    checked={isAllSelected}
                    ref={(el) => {
                      if (el) el.indeterminate = isSomeSelected;
                    }}
                    onChange={() => {}}
                    className="w-4 h-4 cursor-pointer accent-primary"
                  />
                </div>
                <div
                  className="flex-1 flex items-center cursor-pointer font-semibold px-2"
                  onClick={() =>
                    setExpandedGroups((p) => ({ ...p, [catId]: !p[catId] }))
                  }
                >
                  {t(labelKey)}
                  <span className="ml-2 text-xs font-normal text-muted-foreground bg-secondary px-2 py-0.5 rounded-full tabular-nums">
                    {selectedInGroup.length} / {qs.length}
                  </span>
                </div>
                <button
                  className="p-1 text-muted-foreground"
                  onClick={() =>
                    setExpandedGroups((p) => ({ ...p, [catId]: !p[catId] }))
                  }
                >
                  {isExpanded ? (
                    <ChevronDown size={18} />
                  ) : (
                    <ChevronRight size={18} />
                  )}
                </button>
              </div>

              {isExpanded && (
                <div className="p-2 border-t bg-background/50 divide-y divide-secondary/40">
                  {qs.map((q) => {
                    const answer = getItemAnswer(q);
                    const isChecked = selectedKeys.has(q.uniqueKey);
                    return (
                      <div
                        key={q.uniqueKey}
                        onClick={() => toggleItemSelection(q.uniqueKey)}
                        className="flex items-center justify-between gap-3 p-2.5 rounded-lg hover:bg-secondary/50 cursor-pointer text-sm transition"
                      >
                        <div className="flex items-center gap-3 overflow-hidden">
                          <input
                            type="checkbox"
                            checked={isChecked}
                            onChange={() => {}}
                            className="w-4 h-4 ml-4 accent-primary shrink-0 pointer-events-none"
                          />
                          {catId === "alphabet" && q.char && (
                            <button
                              type="button"
                              onClick={(e) => {
                                e.stopPropagation();
                                playAudio("letter", q.char);
                              }}
                              className="w-7 h-7 rounded-full border grid place-items-center shrink-0"
                              aria-label={t("dictionaryword.ecouter")}
                            >
                              <Volume2 size={13} />
                            </button>
                          )}
                          <span
                            className={`truncate ${
                              isChecked
                                ? "text-foreground font-medium"
                                : "text-muted-foreground"
                            }`}
                          >
                            {getItemLabel(q)}
                          </span>
                        </div>
                        {answer && (
                          <span className="text-xs px-2.5 py-1 rounded-md bg-secondary text-muted-foreground shrink-0 max-w-[200px] truncate text-right">
                            {answer}
                          </span>
                        )}
                      </div>
                    );
                  })}
                </div>
              )}
            </div>
          );
        })}
      </div>

      <button
        onClick={start}
        disabled={selectedCount === 0}
        className="w-full py-4 rounded-2xl bg-primary text-primary-foreground text-lg font-bold disabled:opacity-40 flex items-center justify-center gap-2"
      >
        {selectedCount === 0
          ? t("review.selectionne_au_moins_une_notion")
          : t("review.demarrer_session_count", {
              count: Math.min(count, maxCount),
            })}
        <ArrowRight size={18} />
      </button>
    </div>
  );
}
